import {MithraMaterializedApi} from "../../services/MithraMaterializedApi.ts";
import {
    GridFilterModel, GridGetRowsParams, GridRowModel
} from "@mui/x-data-grid-premium";
import {EMPTY_FILTER} from "./MuixDataGridConstants.ts";
import {deepCopy} from "../../utils/js-utils.ts";
import {AxiosResponse} from "axios";
import {MuiXGroupedResponse, MuiXResponse} from "./MuixApiTypes.ts";
import {BaseMuixTableController, SUBGROUP_PAGINATION_PREPEND_ID} from "./controllers/BaseMuixTableController.tsx";
import {NestedRelatedDataModel} from "../../services/ApiHelpers.ts";

export class BaseMuixApi {
    constructor(
        private materializedApi: MithraMaterializedApi,
        private baseUrl: string,
        private tableController: BaseMuixTableController,
    ) {
    }

    public static getMuiXQuery(
        databag: number,
        data: Partial<{
            sortModel: GridGetRowsParams['sortModel'],
            filterModel: GridGetRowsParams['filterModel'] | null,
            groupKeys: GridGetRowsParams['groupKeys'],
            groupFields: GridGetRowsParams['groupFields'],
            aggregationModel: GridGetRowsParams['aggregationModel'],
            relatedData: NestedRelatedDataModel,
            page: number,
            pageSize: number,
            noContent: boolean,
            noPage: boolean,
        }>,
    ): URLSearchParams {
        if (EMPTY_FILTER.items.length > 0) {
            throw new Error("EMPTY_FILTER has been modified");
        }
        const filterModel = data.filterModel ?? deepCopy(EMPTY_FILTER);

        const query = new URLSearchParams();
        query.set('databag', String(databag));
        query.set('filterModel', JSON.stringify(filterModel));
        query.set('sortModel', data.sortModel ? JSON.stringify(data.sortModel) : '[]');
        query.set('groupKeys', data.groupKeys ? JSON.stringify(data.groupKeys) : '[]'); // pro
        query.set('groupFields', data.groupFields ? JSON.stringify(data.groupFields) : '[]'); //  premium
        query.set('aggregationModel', data.aggregationModel ? JSON.stringify(data.aggregationModel) : '{}'); // premium
        if (data.relatedData !== undefined) query.set('related_data', JSON.stringify(data.relatedData));
        if (data.page !== undefined) query.set('page', String(data.page));
        if (data.pageSize !== undefined) query.set('page_size', String(data.pageSize));
        if (data.noContent) query.set('noContent', 'true');
        if (data.noPage) query.set('no_page', '');
        return query;
    }

    /**
     * Get all data (paginated)
     */
    get(
        databag: number,
        params: GridGetRowsParams,
        filter: GridFilterModel | null,
        relatedData: NestedRelatedDataModel,
    ): Promise<AxiosResponse<MuiXGroupedResponse<any>> | AxiosResponse<MuiXResponse<GridRowModel>>> {
        console.debug('BaseMuixApi.getRows()', databag, params, filter, relatedData);
        let pageSize: number;
        let pageIndex: number;
        // Here we ensure groupFields that is being passed to API, is either equal to or one more than groupKeys
        const groupKeys = params.groupKeys ?? [];
        if (groupKeys.length) {
            // We are requesting a subgroup, pagination should be fetched from group override value
            const subGroupPaginationId = SUBGROUP_PAGINATION_PREPEND_ID + groupKeys[groupKeys.length - 1];
            pageSize = this.tableController.getGroupPerPage(subGroupPaginationId);
            pageIndex = this.tableController.getGroupPage(subGroupPaginationId) + 1;
        } else {
            // Convert the pagination to Django-compatible pagination
            const startIndex = Number(params.start); // initial: 0
            const endIndex = Number(params.end); // initial: CATEGORIZATION_TABLE_DEFAULT_PAGE_SIZE - 1
            pageSize = endIndex - startIndex + 1;
            pageIndex = Math.floor(startIndex / pageSize) + 1; // BE pagination starts at 1
        }
        const query = BaseMuixApi.getMuiXQuery(databag, {
            sortModel: params.sortModel,
            // Handle the complex filters in the backend
            filterModel: filter,
            groupKeys: groupKeys,
            groupFields: params.groupFields,
            aggregationModel: params.aggregationModel,
            relatedData,
            page: pageIndex,
            pageSize: pageSize,
        });
        return this.materializedApi.http.get(
            `${this.baseUrl}/grid/`, {params: query}
        ) as Promise<AxiosResponse<MuiXGroupedResponse<any>> | AxiosResponse<MuiXResponse<any>>>;
    }
}
